/**
 * Settings, Agent accounts, as a person thinks of it (ADR 0108 amendment): "connect my ChatGPT",
 * not kinds and pools. Pure logic for the agent tiles, the machine chips and the Connect dialog.
 */
import { LAUNCH_ACCOUNT_AGENT_KINDS, launchAccountAgent, machineSignInAccount } from '@orgus/protocol';
import type { LaunchAccount, LaunchAccountAgent, LaunchAccountKind, LaunchAccountsState } from '@orgus/protocol';
import { formatInt } from '@orgus/ui';
import { accountsOf, foldersOf, machineSignInOf } from './data.ts';

/** How a person connects an agent, in their words. */
export type ConnectWay = 'subscription' | 'api-key' | 'cloud';

export interface ConnectWayInfo {
  way: ConnectWay;
  label: string;
  hint: string;
  /** The account kinds behind it: one, or the cloud providers to pick from. */
  kinds: LaunchAccountKind[];
}

/** The vendor a person connects for each agent: the noun on its Connect button. */
export const CONNECT_VENDOR: Record<LaunchAccountAgent, string> = {
  'claude-code': 'Claude',
  codex: 'ChatGPT',
  'gemini-cli': 'Google',
};

const WAYS: Record<LaunchAccountAgent, Omit<ConnectWayInfo, 'kinds'>[]> = {
  'claude-code': [
    {
      way: 'subscription',
      label: 'Claude subscription',
      hint: 'Pro, Max, Team or Enterprise',
    },
    { way: 'api-key', label: 'Anthropic API key', hint: 'Billed per token' },
    {
      way: 'cloud',
      label: 'Cloud provider',
      hint: 'Bedrock, Vertex AI or Azure',
    },
  ],
  codex: [
    {
      way: 'subscription',
      label: 'ChatGPT plan',
      hint: 'Plus, Pro, Business or Enterprise',
    },
    { way: 'api-key', label: 'OpenAI API key', hint: 'Billed per token' },
  ],
  'gemini-cli': [
    {
      way: 'subscription',
      label: 'Google account',
      hint: 'Personal or Code Assist',
    },
    { way: 'api-key', label: 'Gemini API key', hint: 'Billed per token' },
    { way: 'cloud', label: 'Vertex AI', hint: 'Your Google Cloud project' },
  ],
};

/** The ways an agent connects, the usual one first, each with the kinds behind it. */
export function connectWays(agent: LaunchAccountAgent): ConnectWayInfo[] {
  const kinds = LAUNCH_ACCOUNT_AGENT_KINDS[agent];
  return WAYS[agent]
    .map((w) => ({
      ...w,
      kinds: w.way === 'cloud' ? kinds.filter((k) => k !== 'subscription' && k !== 'api-key') : kinds.filter((k) => k === w.way),
    }))
    .filter((w) => w.kinds.length > 0);
}

/** The way an account connects. */
export function wayOf(a: Pick<LaunchAccount, 'kind'>): ConnectWay {
  return a.kind === 'subscription' ? 'subscription' : a.kind === 'api-key' ? 'api-key' : 'cloud';
}

/** The words for how an account connects: "ChatGPT plan", "Anthropic API key", "Bedrock". */
export function wayLabel(a: Pick<LaunchAccount, 'kind' | 'agent' | 'builtIn'>): string {
  if (a.builtIn) return "Each machine's own sign-in";
  const agent = launchAccountAgent(a);
  const w = wayOf(a);
  if (w !== 'cloud') return WAYS[agent].find((x) => x.way === w)?.label ?? a.kind;
  return CLOUD_LABEL[a.kind] ?? a.kind;
}

export const CLOUD_LABEL: Partial<Record<LaunchAccountKind, string>> = {
  bedrock: 'Amazon Bedrock',
  vertex: 'Vertex AI',
  foundry: 'Azure Foundry',
};

/** One machine's readiness for an account. */
export type MachineReadiness = 'ready' | 'needs-sign-in' | 'signing-in' | 'offline' | 'unknown';

export interface MachineChip {
  machineId: string;
  name: string;
  state: MachineReadiness;
}

export const READINESS_LABEL: Record<MachineReadiness, string> = {
  ready: 'Ready',
  'needs-sign-in': 'Needs sign-in',
  'signing-in': 'Signing in',
  offline: 'Offline',
  unknown: 'Not checked',
};

interface MachineLike {
  id: string;
  name: string;
  online: boolean;
}

/**
 * Whether each machine can run sessions on the account now. A key, a cloud account and a Claude
 * subscription signed in with its token work on every machine; a ChatGPT or Google sign-in and a
 * machine's own sign-in are per machine.
 */
export function machineChips(state: LaunchAccountsState, a: LaunchAccount, machines: readonly MachineLike[]): MachineChip[] {
  const everywhere = a.kind !== 'subscription' || (!!a.secret && !a.builtIn);
  const folders = a.builtIn ? foldersOf(state, a) : [];
  return machines.map((m) => {
    const base = { machineId: m.id, name: m.name };
    if (everywhere) return { ...base, state: 'ready' as const };
    const s = machineSignInOf(state, a.id, m.id);
    if (s && s.step !== 'done' && s.step !== 'failed') return { ...base, state: 'signing-in' as const };
    const path = a.builtIn ? (folders.find((f) => f.machineId === m.id)?.path ?? null) : (a.folders[m.id] ?? null);
    const st = a.builtIn ? folders.find((f) => f.machineId === m.id)?.status : state.folders.find((f) => f.accountId === a.id && f.machineId === m.id && f.path === path);
    if (st?.state === 'signed-in') return { ...base, state: 'ready' as const };
    if (!m.online) return { ...base, state: 'offline' as const };
    if (a.builtIn && !st) return { ...base, state: 'unknown' as const };
    return { ...base, state: 'needs-sign-in' as const };
  });
}

/** The tile's one next step. */
export type TileAction =
  | { kind: 'connect' }
  | {
      kind: 'sign-in';
      accountId: string;
      machineId: string;
      machineName: string;
    }
  | { kind: 'add' };

export interface AgentTile {
  agent: LaunchAccountAgent;
  /** The agent's own accounts (not the built-in sign-in). */
  accounts: LaunchAccount[];
  /** The account the tile shows: the first subscription, else the first account, else the built-in sign-in. */
  lead: LaunchAccount | null;
  chips: MachineChip[];
  action: TileAction;
  /** The bright line: what it runs on. */
  title: string;
  /** The dim line: how many machines are ready. */
  detail: string;
}

/** The tile for one agent: what it runs on, each machine's state, and the one thing to do next. */
export function agentTile(state: LaunchAccountsState, agent: LaunchAccountAgent, machines: readonly MachineLike[]): AgentTile {
  const all = accountsOf(state, agent);
  const accounts = all.filter((a) => !a.builtIn);
  const own = all.find((a) => a.builtIn) ?? null;
  const lead = accounts.find((a) => a.kind === 'subscription') ?? accounts[0] ?? own;
  const chips = lead ? machineChips(state, lead, machines) : [];
  const ready = chips.filter((c) => c.state === 'ready').length;
  const needs = chips.find((c) => c.state === 'needs-sign-in');
  let action: TileAction;
  if (!accounts.length) action = { kind: 'connect' };
  else if (lead && needs && machineSignInAccount(lead))
    action = {
      kind: 'sign-in',
      accountId: lead.id,
      machineId: needs.machineId,
      machineName: needs.name,
    };
  else if (lead && needs && lead.agent === 'claude-code' && lead.kind === 'subscription' && !lead.secret)
    action = {
      kind: 'sign-in',
      accountId: lead.id,
      machineId: needs.machineId,
      machineName: needs.name,
    };
  else action = { kind: 'add' };
  const title = !accounts.length ? (own && ready ? "Each machine's own sign-in" : 'Not connected') : accounts.length === 1 ? accounts[0]!.name : `${lead!.name} and ${accounts.length - 1} more`;
  const detail = !chips.length ? 'No machines yet' : chips.length === 1 ? READINESS_LABEL[chips[0]!.state] : ready === chips.length ? `Ready on all ${formatInt(chips.length)} machines` : `Ready on ${formatInt(ready)} of ${formatInt(chips.length)} machines`;
  return { agent, accounts, lead, chips, action, title, detail };
}
